import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useGame } from "@/context/GameContext";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;
const DARK_BG = { background: "radial-gradient(ellipse at top, #3D0810 0%, #1a0205 40%, #0f0102 100%)" };

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userToken, refreshUser, setCurrentUser } = useGame();
  const [status, setStatus] = useState("checking");
  const [planName, setPlanName] = useState(null);
  const [gameCredits, setGameCredits] = useState(null);

  useEffect(() => {
    const isCanceled = window.location.pathname.includes("canceled");
    if (isCanceled) {
      setStatus("canceled");
      localStorage.removeItem("tamara_order_id");
      localStorage.removeItem("salla_purchase_id");
      return;
    }

    // --- Salla flow ---
    const sallaPurchaseId = localStorage.getItem("salla_purchase_id");
    if (sallaPurchaseId) {
      const verifySalla = async () => {
        try {
          if (!userToken) { setStatus("pending"); setTimeout(verifySalla, 1500); return; }

          const { data } = await axios.get(`${API}/salla/status/${sallaPurchaseId}`, {
            headers: { Authorization: `Bearer ${userToken}` },
          });

          if (data.status === "granted") {
            localStorage.removeItem("salla_purchase_id");
            if (data.user?.id) {
              setCurrentUser(data.user);
              localStorage.setItem("hujjah_user", JSON.stringify(data.user));
              if (data.user.last_plan_name) setPlanName(data.user.last_plan_name);
              if (data.user.game_credits)   setGameCredits(data.user.game_credits);
            } else {
              if (refreshUser) await refreshUser();
            }
            setStatus("success");
          } else if (["manual_review", "failed"].includes(data.status)) {
            localStorage.removeItem("salla_purchase_id");
            setStatus("error");
          } else {
            // pending — retry
            setStatus("pending");
            setTimeout(verifySalla, 3000);
          }
        } catch {
          setStatus("error");
        }
      };
      verifySalla();
      return;
    }

    // --- Tamara flow (existing) ---
    const orderIdFromUrl   = searchParams.get("orderId") || searchParams.get("order_id");
    const orderIdFromStore = localStorage.getItem("tamara_order_id");
    const orderId          = orderIdFromUrl || orderIdFromStore;

    if (!orderId) {
      setStatus("error");
      return;
    }

    const verify = async () => {
      try {
        // Token might not be ready yet on first render — retry shortly
        if (!userToken) { setStatus("pending"); setTimeout(verify, 1500); return; }

        const { data } = await axios.get(`${API}/tamara/verify/${orderId}`, {
          headers: { Authorization: `Bearer ${userToken}` },
        });

        if (data.status === "paid") {
          localStorage.removeItem("tamara_order_id");

          // Update React context immediately — no logout/login needed
          if (data.user?.id) {
            // Backend returned a valid fresh user object — update context directly
            setCurrentUser(data.user);
            localStorage.setItem("hujjah_user", JSON.stringify(data.user));
            if (data.user.last_plan_name) setPlanName(data.user.last_plan_name);
            if (data.user.game_credits)   setGameCredits(data.user.game_credits);
          } else {
            // Fallback: pull fresh data from /auth/me (covers edge cases)
            if (refreshUser) await refreshUser();
          }

          // If this was a tournament purchase, redirect back to tournament setup
          const pendingTournament = localStorage.getItem("pending_tournament_plan");
          if (pendingTournament && pendingTournament.startsWith("tournament_")) {
            localStorage.removeItem("pending_tournament_plan");
            const sizeStr = pendingTournament.replace("tournament_", "");
            const sizeNum = parseInt(sizeStr, 10);
            if (!isNaN(sizeNum)) {
              navigate("/tournament", { state: { autoStartSize: sizeNum } });
              return;
            }
          }

          setStatus("success");

        } else if (["declined", "canceled", "expired", "failed"].includes(data.status)) {
          localStorage.removeItem("tamara_order_id");
          setStatus("canceled");
        } else {
          // Still pending — retry automatically after 3s
          setStatus("pending");
          setTimeout(verify, 3000);
        }
      } catch {
        setStatus("error");
      }
    };

    verify();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const messages = {
    checking: { icon: "⏳", title: "جاري التحقق من الدفع...", sub: "لحظة واحدة" },
    success:  { icon: "✓",  title: "تم الاشتراك بنجاح!", sub: planName ? `باقة ${planName}` : "مرحباً بك في حُجّة المميز!" },
    pending:  { icon: "⏳", title: "الدفع قيد المعالجة", sub: "يتم التحقق تلقائياً..." },
    canceled: { icon: "✕",  title: "تم إلغاء الدفع", sub: "لم يتم خصم أي مبلغ من حسابك" },
    error:    { icon: "✕",  title: "حدث خطأ", sub: "تواصل مع الدعم إذا تم خصم المبلغ" },
  };

  const m = messages[status];

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={DARK_BG}>
      <div className="relative z-10 text-center max-w-sm w-full">
        <div
          className={`w-24 h-24 rounded-full flex items-center justify-center text-4xl mx-auto mb-6 border-2 ${
            status === "success" ? "border-green-400 bg-green-900/30 text-green-400" :
            status === "error"   ? "border-red-400 bg-red-900/30 text-red-400" :
            "border-secondary/40 bg-primary/50 text-secondary"
          }`}
        >
          {m.icon}
        </div>
        <h1 className="text-secondary font-black text-2xl mb-2">{m.title}</h1>
        <p className="text-secondary/60 text-sm mb-8">{m.sub}</p>

        {status === "success" && (
          <div className="space-y-4">
            {gameCredits && (
              <div style={{
                background: "rgba(241,225,148,.10)",
                border: "1px solid rgba(241,225,148,.25)",
                borderRadius: 12,
                padding: "12px 20px",
                marginBottom: 8,
              }}>
                <span className="text-secondary font-black text-lg">{gameCredits} 🎮</span>
                <p className="text-secondary/60 text-xs mt-1">لعبة متاحة الآن</p>
              </div>
            )}
            <button
              data-testid="go-home-btn"
              onClick={() => navigate("/")}
              className="bg-secondary text-primary font-black px-10 py-3 rounded-full hover:scale-105 transition-all w-full"
            >
              العب الحين ⚡
            </button>
          </div>
        )}

        {status === "pending" && (
          <div className="space-y-3">
            <div className="flex justify-center gap-1">
              {[0,1,2].map(i => (
                <div key={i} style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: "rgba(241,225,148,.6)",
                  animation: `pulse 1.2s ease-in-out ${i*0.2}s infinite`,
                }} />
              ))}
            </div>
            <button onClick={() => navigate("/")} className="text-secondary/40 text-sm hover:text-secondary transition-colors mt-4 block">
              العودة للرئيسية
            </button>
          </div>
        )}

        {(status === "canceled" || status === "error") && (
          <button onClick={() => navigate("/pricing")} className="text-secondary/60 hover:text-secondary text-sm transition-colors">
            العودة للاشتراك
          </button>
        )}
      </div>
    </div>
  );
}
